/**
 * @file commonISRedux - Common Infinite Scroll Redux Slice
 * @description Shared Redux slice for DataViewInfiniteScroll components using common Redux mode.
 * 
 * PURPOSE (from common-redux-migration-plan.md):
 * - Allow DataViewInfiniteScroll to use a common global Redux slice for infinite scroll
 * - Eliminate the need to create a new Redux slice for each view adopting infinite scroll
 * - Support separate main/total queries per entity
 * 
 * ARCHITECTURE (RQ-01: New Architecture):
 * State Structure:
 * {
 *   entities: {
 *     [entity]: {
 *       items: [],           // Main query results (appended on load more)
 *       loading: false,      // Main query loading state
 *       nextToken: null,     // Pagination cursor for infinite scroll
 *       error: null,         // Error from last fetch
 *       totalCount: 0,       // Separate total count
 *       totalCountLoading: false,
 *       totalCountError: null, // Error from total count fetch (for retry UI)
 *       lastConditions: null // Track for deduplication
 *     }
 *   }
 * }
 * 
 * @see common-redux-migration-plan.md for full implementation plan
 * @since 2026-03-04
 */

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { clientQuery } from 'Core/data/GraphQLApi';
import { endpoints } from 'Core/defaultValues';
import { ExceptionManager } from 'Core/logManager';
import _ from 'lodash';

// RQ-02: Slice name constant for namespacing
const SLICE_NAME = 'commonInfiniteScroll';

// Module-level AbortController storage for request cancellation (Option C)
// Maps entity key to AbortController for that entity's in-flight request
const inflightControllers = new Map();

/**
 * Abort any in-flight request for the given entity.
 * Called before starting a new request to cancel stale ones.
 * @param {string} entity - Entity key
 */
export const abortEntityRequest = (entity) => {
    if (inflightControllers.has(entity)) {
        inflightControllers.get(entity).abort();
        inflightControllers.delete(entity);
    }
};

// RQ-03: Initial state structure for the slice
const INIT_STATE = {
    entities: {}
};

// RQ-04: Initial state template for each entity (used when entity is first accessed)
const ENTITY_INIT_STATE = {
    items: [],              // Main query results (appended on load more)
    loading: false,         // Main query loading state
    nextToken: null,        // Pagination cursor for infinite scroll
    error: null,            // Error from last fetch
    totalCount: null,       // Separate total count (null = never fetched, 0 = endpoint returned zero)
    totalCountLoading: false,
    totalCountError: null,  // Error from total count fetch (enables retry UI)
    lastConditions: null,   // Track conditions for deduplication
    lastSort: null,         // Track sort for deduplication
};

/**
 * RQ-05: Async thunk for fetching main data (items) with infinite scroll support.
 * 
 * Supports:
 * - Initial load: Replaces items array
 * - Load more (isLoadMore=true): Appends to existing items
 * - Deduplication: Prevents duplicate requests with same conditions
 * - Request cancellation: Aborts previous request when new one starts with different params
 * 
 * @param {Object} params - Fetch parameters
 * @param {string} params.entity - Entity key for namespacing
 * @param {string} params.query - GraphQL query string
 * @param {Object} params.variables - GraphQL variables { limit, offset, sort, conditions }
 * @param {boolean} params.isLoadMore - Whether this is a load-more operation (append vs replace)
 */
export const fetchInfiniteScrollData = createAsyncThunk(
    `${SLICE_NAME}/fetchData`,
    async ({ entity, query, variables, isLoadMore }, { rejectWithValue }) => {
        try {
            // Option C: Abort any previous in-flight request for this entity
            abortEntityRequest(entity);
            
            // Create new AbortController for this request
            const controller = new AbortController();
            inflightControllers.set(entity, controller);
            
            // RQ-05a: Execute GraphQL query with abort signal
            const data = await clientQuery(
                query, 
                variables, 
                endpoints.GRAPHQL_GENERAL,
                undefined,  // headers
                controller.signal  // abort signal
            );
            
            // Clean up controller after successful completion
            inflightControllers.delete(entity);
            
            return {
                entity,
                items: data?.result?.items || [],
                nextToken: data?.result?.nextToken || null,
                isLoadMore,
            };
        } catch (exc) {
            // Clean up controller on error
            inflightControllers.delete(entity);
            
            // RQ-05b: Handle aborted requests gracefully
            if (exc.name === 'AbortError' || exc.aborted) {
                return rejectWithValue({ 
                    entity, 
                    error: null,  // null error = aborted, not a real error
                    aborted: true 
                });
            }
            
            // RQ-05c: Return error with entity for proper state update
            return rejectWithValue({ 
                entity, 
                error: exc.message || 'Failed to fetch infinite scroll data' 
            });
        }
    },
    {
        // RQ-05d: Condition to prevent duplicate requests
        // Prevents firing if already loading with same conditions (but allows different params)
        condition: ({ entity, variables }, { getState }) => {
            const state = getState().commonISRedux?.entities?.[entity];
            if (state?.loading) {
                // Only block if same conditions (deep compare)
                const sameConditions = _.isEqual(variables?.conditions, state.lastConditions);
                const sameSort = _.isEqual(variables?.sort, state.lastSort);
                if (sameConditions && sameSort) {
                    return false; // Prevent duplicate calls
                }
                // Different conditions/sort: allow the new request (previous will be aborted)
            }
            return true;
        }
    }
);

/**
 * RQ-06: Async thunk for fetching total count (separate from main query).
 * 
 * This is called separately from main data fetch because:
 * - Total count queries can be expensive
 * - Only needed on filter changes, not sort-only changes
 * - Not needed on load-more operations
 * 
 * @param {Object} params - Fetch parameters
 * @param {string} params.entity - Entity key for namespacing
 * @param {string} params.query - GraphQL query string (minimal, just total)
 * @param {Object} params.variables - GraphQL variables { conditions, sort, limit, offset }
 */
export const fetchInfiniteScrollTotalCount = createAsyncThunk(
    `${SLICE_NAME}/fetchTotalCount`,
    async ({ entity, query, variables }, { rejectWithValue }) => {
        try {
            // RQ-06a: Execute GraphQL query for total count
            const data = await clientQuery(query, variables, endpoints.GRAPHQL_GENERAL);
            
            // RQ-06a-FIX: Check for GraphQL errors in response body
            // GraphQL can return HTTP 200 with { result: null, errors: [...] }
            // This happens when the query fails at GraphQL level (e.g., timeout, permission)
            if (!data || data.result === null || data.result === undefined) {
                return rejectWithValue({
                    entity,
                    error: 'Total count query returned no result'
                });
            }
            
            return { 
                entity, 
                total: data.result.total || 0 
            };
        } catch (exc) {
            // RQ-06b: Return error with entity for proper state update
            return rejectWithValue({ 
                entity, 
                error: exc.message || 'Failed to fetch total count' 
            });
        }
    },
    {
        // RQ-06c: Condition to prevent duplicate total count requests
        // Prevents firing if already loading total count for this entity
        condition: ({ entity }, { getState }) => {
            const state = getState().commonISRedux?.entities?.[entity];
            if (state?.totalCountLoading) {
                return false; // Prevent duplicate calls while loading
            }
            return true;
        }
    }
);

//
/**
 * Search data for infinite scroll (executed by DataViewIS SearchInput component).
 * @param {Object} params - Search parameters
 * @param {string} params.query - GraphQL query string
 * @param {string} params.graphqlConditionsType - GraphQL conditions type (e.g., "SearchableDeviceConditionInput")
 * @param {Array} params.conditions - Array of condition fields
 * @param {string} params.id - ID field name
 * @param {string} params.label - Label field name
 * @param {string} params.value - Search value
 * @param {Object|Array} params.customCondition - Custom condition(s) to append
 * @param {string} params.extraField - Extra field to include in results
 * @param {number} params.limit - Number of items to fetch
 * @returns {Promise<Object>} - GraphQL query result

 */
export const searchDataIS = async ({
    query,
    graphqlConditions,
    conditions,
    id,
    label,
    value,
    customCondition,
    extraField,
    limit = 30,
}) => {

    try {
        let newConditions = [{
            fields: conditions || [],
            exact: false,
            terms: [value]
        }]

        if (Array.isArray(customCondition))
            newConditions = [...newConditions, ...customCondition];
        else
            if (customCondition) newConditions.push(customCondition);

        return await clientQuery(
            `query ${query} ($conditions: [${graphqlConditions}], $limit: Int, $offset: Int) {
                result: ${query} (conditions: $conditions, limit: $limit, offset: $offset) {
                    items {${id} ${label} ${extraField ? extraField : ""}}
                }
            }
            `,
            {
                conditions: newConditions,
                limit: limit,
                offset: 0
            },
            endpoints.GRAPHQL_GENERAL,
        );

    } catch (exc) {
        ExceptionManager(exc, 'Core/common/redux/saga', 'searchDataIS');
    }
};

/**
 * RQ-07: Create the Redux slice with reducers and extraReducers
 */
export const commonInfiniteScrollSlice = createSlice({
    name: SLICE_NAME,
    initialState: INIT_STATE,
    reducers: {
        /**
         * RQ-08: Initialize entity state if not exists.
         * Called when component mounts to ensure entity exists in state.
         */
        initEntity(state, action) {
            const { entity } = action.payload;
            if (!state.entities[entity]) {
                state.entities[entity] = { ...ENTITY_INIT_STATE };
            }
        },
        
        /**
         * RQ-09: Clear entity state (reset to initial).
         * Called on unmount if clearOnUnmount prop is true (default).
         */
        clearEntity(state, action) {
            const { entity } = action.payload;
            if (state.entities[entity]) {
                state.entities[entity] = { ...ENTITY_INIT_STATE };
            }
        },
        
        /**
         * RQ-10: Reset all entities (clear entire slice).
         * Useful for logout or full app reset scenarios.
         */
        resetAllEntities(state) {
            state.entities = {};
        },

        /**
         * Reset total count fields for an entity.
         * Called when showTotalCount transitions true → false (e.g., Range → Last mode switch)
         * to clear stale cached total so it doesn't flash on the next Range entry.
         */
        resetEntityTotalCount(state, action) {
            const { entity } = action.payload;
            if (state.entities[entity]) {
                state.entities[entity].totalCount = null;
                state.entities[entity].totalCountLoading = false;
                state.entities[entity].totalCountError = null;
            }
        },
        
        /**
         * RQ-11: Manually set items for an entity.
         * Useful for optimistic updates or external data injection.
         */
        setEntityItems(state, action) {
            const { entity, items, nextToken } = action.payload;
            if (!state.entities[entity]) {
                state.entities[entity] = { ...ENTITY_INIT_STATE };
            }
            state.entities[entity].items = items;
            if (nextToken !== undefined) {
                state.entities[entity].nextToken = nextToken;
            }
        },
    },
    extraReducers: (builder) => {
        builder
            // ═══════════════════════════════════════════════════════════════════════
            // RQ-12: Main data fetch lifecycle handlers
            // ═══════════════════════════════════════════════════════════════════════
            
            // RQ-12a: Pending - Set loading state and track conditions
            .addCase(fetchInfiniteScrollData.pending, (state, action) => {
                const { entity, variables, isLoadMore } = action.meta.arg;
                
                // Initialize entity if not exists
                if (!state.entities[entity]) {
                    state.entities[entity] = { ...ENTITY_INIT_STATE };
                }
                
                state.entities[entity].loading = true;
                state.entities[entity].error = null;
                
                // Track conditions and sort for deduplication
                state.entities[entity].lastConditions = variables?.conditions;
                state.entities[entity].lastSort = variables?.sort;
                
                // BUG-FIX: Clear items immediately when NOT a load-more operation
                // This ensures UI shows empty state while fetching on filter/sort/refresh changes
                // instead of keeping stale data visible until API response arrives
                if (!isLoadMore) {
                    state.entities[entity].items = [];
                    state.entities[entity].nextToken = null;
                }
            })
            
            // RQ-12b: Fulfilled - Update items (append or replace based on isLoadMore)
            .addCase(fetchInfiniteScrollData.fulfilled, (state, action) => {
                const { entity, items, nextToken, isLoadMore } = action.payload;
                
                state.entities[entity].loading = false;
                
                // RQ-12c: Append on load more, replace otherwise
                if (isLoadMore) {
                    // Append new items to existing items
                    state.entities[entity].items = [
                        ...state.entities[entity].items,
                        ...items
                    ];
                } else {
                    // Replace items (initial load or filter/sort change)
                    state.entities[entity].items = items;
                }
                
                state.entities[entity].nextToken = nextToken;
            })
            
            // RQ-12d: Rejected - Set error state (but ignore aborted requests)
            .addCase(fetchInfiniteScrollData.rejected, (state, action) => {
                const payload = action.payload || action.meta.arg;
                const { entity, error, aborted } = payload;
                
                // Option C: Ignore aborted requests - they were intentionally cancelled
                if (aborted) {
                    // Don't update state for cancelled requests - a new request is in flight
                    console.debug(`[infiniteScrollSlice] Request aborted for entity: ${entity}`);
                    return;
                }
                
                if (state.entities[entity]) {
                    state.entities[entity].loading = false;
                    state.entities[entity].error = error || 'Unknown error occurred';
                }
            })
            
            // ═══════════════════════════════════════════════════════════════════════
            // RQ-13: Total count fetch lifecycle handlers
            // ═══════════════════════════════════════════════════════════════════════
            
            // RQ-13a: Pending - Set total count loading and clear previous error
            .addCase(fetchInfiniteScrollTotalCount.pending, (state, action) => {
                const { entity } = action.meta.arg;
                
                // Initialize entity if not exists
                if (!state.entities[entity]) {
                    state.entities[entity] = { ...ENTITY_INIT_STATE };
                }
                
                state.entities[entity].totalCountLoading = true;
                // Clear previous error on new fetch attempt
                state.entities[entity].totalCountError = null;
            })
            
            // RQ-13b: Fulfilled - Update total count and clear error
            .addCase(fetchInfiniteScrollTotalCount.fulfilled, (state, action) => {
                const { entity, total } = action.payload;
                
                state.entities[entity].totalCountLoading = false;
                state.entities[entity].totalCount = total;
                // Clear error on success (in case previous fetch had error)
                state.entities[entity].totalCountError = null;
            })
            
            // RQ-13c: Rejected - Set total count error for retry UI
            .addCase(fetchInfiniteScrollTotalCount.rejected, (state, action) => {
                const payload = action.payload || action.meta.arg;
                const { entity, error } = payload;
                
                if (state.entities[entity]) {
                    state.entities[entity].totalCountLoading = false;
                    // Set error so UI can show retry button/indicator
                    state.entities[entity].totalCountError = error || 'Failed to fetch total count';
                }
            });
    }
});

// ═══════════════════════════════════════════════════════════════════════════════
// RQ-14: Export actions and reducer
// ═══════════════════════════════════════════════════════════════════════════════

export const { 
    initEntity, 
    clearEntity, 
    resetAllEntities,
    resetEntityTotalCount,
    setEntityItems,
} = commonInfiniteScrollSlice.actions;

export default commonInfiniteScrollSlice.reducer;

// ═══════════════════════════════════════════════════════════════════════════════
// RQ-15: Selectors for accessing entity state
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * RQ-15a: Select entire entity state for a given entity key.
 * Returns ENTITY_INIT_STATE if entity doesn't exist (safe access).
 * 
 * @param {string} entity - Entity key
 * @returns {Function} Selector function for Redux useSelector
 * 
 * @example
 * const entityState = useSelector(selectEntityState('devices-main'));
 * // Returns: { items, loading, nextToken, error, totalCount, totalCountLoading, totalCountError }
 */
export const selectEntityState = (entity) => (state) => 
    state.commonISRedux?.entities?.[entity] || ENTITY_INIT_STATE;